
import { useState, useMemo } from 'react';
import type { TargetAudience } from '../../types';

interface UseAudienceStepProps {
    audiences: TargetAudience[];
    onSelect: (selectedIds: string[]) => void;
    onAnalyze?: (customInput?: string) => Promise<void>;
    customInput?: string;
}

export const useAudienceStep = ({
    audiences,
    onSelect,
    onAnalyze,
    customInput
}: UseAudienceStepProps) => {
    const [showCustomInput, setShowCustomInput] = useState(false);

    // 获取选中的人群ID
    const selectedIds = useMemo(() =>
        audiences.filter(a => a.selected).map(a => a.id),
        [audiences]
    );

    // 切换选择
    const handleToggle = (id: string) => {
        const newSelected = selectedIds.includes(id)
            ? selectedIds.filter(i => i !== id)
            : [...selectedIds, id];
        onSelect(newSelected);
    };

    // 全选/取消全选
    const handleSelectAll = () => {
        if (selectedIds.length === audiences.length) {
            onSelect([]);
        } else {
            onSelect(audiences.map(a => a.id));
        }
    };

    // 按类别分组
    const groupedAudiences = useMemo(() => audiences.reduce((acc, aud) => {
        const category = aud.category || '其他';
        if (!acc[category]) acc[category] = [];
        acc[category].push(aud);
        return acc;
    }, {} as Record<string, TargetAudience[]>), [audiences]);

    // 重新分析
    const handleReAnalyze = async () => {
        if (onAnalyze) {
            await onAnalyze(customInput);
        }
    };

    return {
        showCustomInput,
        setShowCustomInput,
        selectedIds,
        groupedAudiences,
        handleToggle,
        handleSelectAll,
        handleReAnalyze
    };
};
